import { deleteToken, getToken } from "firebase/messaging";
import { getFirebaseMessaging, VAPID_KEY } from "./config";
import { signOutSellerFirebaseAuth } from "./auth-session";
import { ensureEffectivelyOnline } from "./connectivity";
import { apiFetchClient } from "../api/client";
import { endpoints } from "../api/endpoints";

const FCM_TOKEN_KEY_PREFIX = "a2z_fcm_token";
const FCM_PENDING_CLEANUP_KEY = "a2z_fcm_pending_cleanup";
let notificationGeneration = 0;
let registrationAllowed = true;
const activeRegistrationAttempts = new Set<Promise<unknown>>();

/** Max time we allow `getToken` to run before treating it as a transient timeout. */
const GET_TOKEN_TIMEOUT_MS = 10_000;

function storageKey(storeId?: string): string {
  return storeId ? `${FCM_TOKEN_KEY_PREFIX}_${storeId}` : FCM_TOKEN_KEY_PREFIX;
}

function storedTokenEntries(): [string, string][] {
  const entries: [string, string][] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (
      key !== FCM_TOKEN_KEY_PREFIX &&
      !key?.startsWith(`${FCM_TOKEN_KEY_PREFIX}_`)
    ) {
      continue;
    }
    const token = localStorage.getItem(key);
    if (token) entries.push([key, token]);
  }
  return entries;
}

function clearStoredFcmTokens(): void {
  for (const [key] of storedTokenEntries()) localStorage.removeItem(key);
}

function clearStoredFcmToken(token: string): void {
  for (const [key, storedToken] of storedTokenEntries()) {
    if (storedToken === token) localStorage.removeItem(key);
  }
}

function pendingCleanupTokens(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(FCM_PENDING_CLEANUP_KEY) ?? "[]");
    return Array.isArray(parsed)
      ? [...new Set(parsed.filter((value): value is string => typeof value === "string"))]
      : [];
  } catch {
    return [];
  }
}

function writePendingCleanup(tokens: readonly string[]): void {
  const unique = [...new Set(tokens)];
  if (unique.length === 0) localStorage.removeItem(FCM_PENDING_CLEANUP_KEY);
  else localStorage.setItem(FCM_PENDING_CLEANUP_KEY, JSON.stringify(unique));
}

async function deleteSellerFcmToken(token: string): Promise<boolean> {
  try {
    const response = await apiFetchClient(endpoints.seller.fcmTokens, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function retrySellerFcmTokenCleanup(): Promise<void> {
  const failed: string[] = [];
  for (const token of pendingCleanupTokens()) {
    if (await deleteSellerFcmToken(token)) clearStoredFcmToken(token);
    else failed.push(token);
  }
  writePendingCleanup(failed);
}

/**
 * Classified reason a token request failed. Surfaced to the provider (Phase 2)
 * so it can decide whether to retry (transient) or stop (terminal).
 *  - `config`     — Firebase messaging / VAPID not configured (terminal).
 *  - `offline`    — browser offline or Firebase Installations app-offline (transient).
 *  - `network`    — fetch/network failure reaching FCM (transient).
 *  - `timeout`    — `getToken` exceeded the timeout (transient).
 *  - `permission` — notification permission blocked/denied (terminal).
 *  - `unknown`    — anything else (transient by default at the provider).
 */
export type FcmErrorReason =
  | "config"
  | "offline"
  | "network"
  | "timeout"
  | "permission"
  | "unknown";

/** Discriminated result of a token request. */
export type FcmTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: FcmErrorReason; error?: unknown };

export type SellerFcmRegistrationResult =
  | { status: "registered"; token: string }
  | { status: "already-registered"; token: string }
  | { status: "failed"; reason: FcmErrorReason }
  | { status: "cancelled" };

type SellerFcmRegistrationDependencies = {
  requestToken: () => Promise<FcmTokenResult>;
  registerToken: (token: string, storeId?: string) => Promise<boolean>;
};

/** A freshly mounted authenticated seller session may start registrations. */
export function activateSellerNotificationSession(): void {
  notificationGeneration += 1;
  registrationAllowed = true;
}

/** Prevent a provider that is leaving the tree from completing a late registration. */
export function invalidateSellerNotificationSession(): void {
  notificationGeneration += 1;
  registrationAllowed = false;
}

/**
 * Own the whole getToken -> backend POST sequence so logout can invalidate it
 * before either boundary and wait for a POST already on the wire.
 */
export function registerSellerFcmForActiveSession(
  storeId: string | undefined,
  isAlreadyRegistered: (token: string) => boolean,
  dependencies: SellerFcmRegistrationDependencies = {
    requestToken: requestFcmToken,
    registerToken: registerFcmTokenWithBackend,
  },
): Promise<SellerFcmRegistrationResult> {
  const generation = notificationGeneration;
  const operation = (async (): Promise<SellerFcmRegistrationResult> => {
    if (!registrationAllowed) return { status: "cancelled" };
    const tokenResult = await dependencies.requestToken();
    if (
      generation !== notificationGeneration ||
      !registrationAllowed
    ) {
      return { status: "cancelled" };
    }
    if (!tokenResult.ok) {
      return { status: "failed", reason: tokenResult.reason };
    }
    if (isAlreadyRegistered(tokenResult.token)) {
      return { status: "already-registered", token: tokenResult.token };
    }
    const registered = await dependencies.registerToken(
      tokenResult.token,
      storeId,
    );
    if (
      generation !== notificationGeneration ||
      !registrationAllowed
    ) {
      return { status: "cancelled" };
    }
    return registered
      ? { status: "registered", token: tokenResult.token }
      : { status: "failed", reason: "network" };
  })();
  activeRegistrationAttempts.add(operation);
  void operation.then(
    () => activeRegistrationAttempts.delete(operation),
    () => activeRegistrationAttempts.delete(operation),
  );
  return operation;
}

/** Tag used so the timeout branch of `Promise.race` is identifiable. */
const FCM_TIMEOUT_TAG = "fcm-get-token-timeout";

export async function withFcmOperationTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(FCM_TIMEOUT_TAG)),
          GET_TOKEN_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/** Defensive, type-safe reads of common error shapes (no `any`). */
function errorCode(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

function errorName(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { name?: unknown }).name;
    return typeof name === "string" ? name : undefined;
  }
  return undefined;
}

function errorMessage(error: unknown): string | undefined {
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

/**
 * Map a thrown error to a {@link FcmErrorReason}. Reads `.code`/`.name`/`.message`
 * defensively. Ordering matters: offline is checked before generic network because
 * the Installations SDK throws `installations/app-offline` when `navigator.onLine`
 * is false (the root cause this change heals).
 */
export function classifyFcmError(error: unknown): FcmErrorReason {
  const code = errorCode(error);
  const name = errorName(error);
  const message = errorMessage(error);

  // Offline: Firebase Installations app-offline, or the browser reports offline.
  if (
    code === "installations/app-offline" ||
    (typeof navigator !== "undefined" && navigator.onLine === false)
  ) {
    return "offline";
  }

  // Permission blocked/denied.
  if (
    code === "messaging/permission-blocked" ||
    code === "messaging/permission-default" ||
    name === "NotAllowedError"
  ) {
    return "permission";
  }

  // Network failure reaching FCM (fetch rejects with a TypeError).
  if (
    name === "TypeError" ||
    (typeof message === "string" && /failed to fetch|network/i.test(message))
  ) {
    return "network";
  }

  return "unknown";
}

/**
 * Request an FCM registration token.
 *
 * Single attempt by design: the central FcmRegistrationProvider (Phase 2) owns
 * retry/backoff so we avoid double-retry here. The `getToken` call is wrapped in
 * a timeout via `Promise.race` because the Firebase SDK call has no built-in
 * timeout and can hang on a stuck service worker / push endpoint.
 *
 * Returns a discriminated result so callers can react to the failure reason
 * instead of a bare null.
 */
export async function requestFcmToken(): Promise<FcmTokenResult> {
  // The service worker is only emitted for production builds (VitePWA
  // `devOptions.enabled: false`), so registering it in dev fetches the SPA
  // fallback HTML and logs "unsupported MIME type ('text/html')" on every
  // load. PwaBootstrap already sits this out in dev; do the same here.
  if (!import.meta.env.PROD) {
    return { ok: false, reason: "config" };
  }

  const messaging = getFirebaseMessaging();
  if (!messaging || !VAPID_KEY) {
    return { ok: false, reason: "config" };
  }

  // `navigator.onLine` is unreliable and can be stuck `false` while the network
  // actually works; Firebase's Installations SDK trusts it and throws
  // `installations/app-offline`. Don't trust a `false` flag — probe real
  // connectivity, and if we're genuinely online, force the flag so getToken can
  // proceed. Only a probe failure (truly offline) short-circuits here.
  if (!(await ensureEffectivelyOnline())) {
    return { ok: false, reason: "offline" };
  }

  try {
    const reg = await withFcmOperationTimeout(
      navigator.serviceWorker.register("/seller-sw.js"),
    );

    const tokenPromise = getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });

    const token = await withFcmOperationTimeout(tokenPromise);

    if (!token) {
      // SDK resolved with an empty token — treat as unknown transient failure.
      console.warn("[fcm] getToken returned empty token");
      return { ok: false, reason: "unknown" };
    }

    return { ok: true, token };
  } catch (error) {
    if (error instanceof Error && error.message === FCM_TIMEOUT_TAG) {
      console.warn("[fcm] getToken timed out", {
        reason: "timeout",
        timeoutMs: GET_TOKEN_TIMEOUT_MS,
      });
      return { ok: false, reason: "timeout" };
    }

    const reason = classifyFcmError(error);
    console.warn("[fcm] getToken failed", { reason, error });
    return { ok: false, reason, error };
  }
}

/**
 * POST the token to the backend. The backend derives the store from the JWT, so
 * the body stays `{ token }` (storeId is NOT sent). `apiFetchClient` returns a
 * `Response` and only rejects on a network error (never on a non-2xx), so success
 * is gated on `response.ok`.
 *
 * Returns `true` only on a 2xx. The localStorage token cache is updated ONLY on
 * success so a failed POST is not mistaken for a registered token.
 */
export async function registerFcmTokenWithBackend(
  token: string,
  storeId?: string,
): Promise<boolean> {
  const previousToken = localStorage.getItem(storageKey(storeId));
  try {
    const response = await apiFetchClient(endpoints.seller.fcmTokens, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      console.warn("[fcm] token registration failed", {
        status: response.status,
      });
      return false;
    }

    localStorage.setItem(storageKey(storeId), token);
    if (previousToken && previousToken !== token) {
      writePendingCleanup([...pendingCleanupTokens(), previousToken]);
    }
    await retrySellerFcmTokenCleanup();
    return true;
  } catch (error) {
    console.warn("[fcm] token registration request failed", { error });
    return false;
  }
}

export async function deregisterFcmTokenFromBackend(
  storeId?: string,
): Promise<boolean> {
  // Historical callers omitted storeId even though registration stores under
  // a per-store key. Treat omission as owner-wide browser cleanup, matching the
  // backend DELETE's owner-wide semantics and avoiding the dead legacy-key
  // lookup that left every real token registered after logout.
  const entries = storeId
    ? [[storageKey(storeId), localStorage.getItem(storageKey(storeId))] as const]
    : storedTokenEntries();
  const tokens = [
    ...new Set(
      [
        ...entries.flatMap(([, token]) =>
          typeof token === "string" ? [token] : [],
        ),
        ...(storeId ? [] : pendingCleanupTokens()),
      ],
    ),
  ];
  let allSucceeded = true;
  for (const token of tokens) {
    try {
      const response = await apiFetchClient(endpoints.seller.fcmTokens, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        allSucceeded = false;
        console.warn("FCM token deregistration failed", {
          status: response.status,
        });
        continue;
      }
      for (const [key, storedToken] of storedTokenEntries()) {
        if (storedToken === token) localStorage.removeItem(key);
      }
      writePendingCleanup(
        pendingCleanupTokens().filter((pending) => pending !== token),
      );
    } catch (error) {
      allSucceeded = false;
      console.warn("FCM token deregistration failed", error);
    }
  }
  return allSucceeded;
}

/**
 * Revoke browser-side delivery when authenticated backend cleanup is
 * unavailable. `deleteToken` removes Firebase's registration; direct Push API
 * unsubscribe is a defensive fallback. If both fail, unregistering this app's
 * worker prevents it from displaying notifications after logout.
 */
export async function disableLocalSellerPushDelivery(): Promise<void> {
  const registration =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? await navigator.serviceWorker
          .getRegistration("/seller-sw.js")
          .catch(() => undefined)
      : undefined;
  const messaging = getFirebaseMessaging();
  const firebaseDeleted = messaging
    ? await deleteToken(messaging).catch(() => false)
    : false;
  const subscription = await registration?.pushManager
    .getSubscription()
    .catch(() => null);
  const unsubscribed = subscription
    ? await subscription.unsubscribe().catch(() => false)
    : true;
  if (!firebaseDeleted && !unsubscribed) {
    await registration?.unregister().catch(() => false);
  }
}

/** Central privacy boundary used by every seller logout path. */
export async function prepareSellerNotificationLogout(): Promise<void> {
  // Close registration before taking the active-attempt snapshot. A late
  // getToken result cannot POST; a POST already on the wire is awaited so its
  // newly stored token is included by the following owner-wide DELETE.
  notificationGeneration += 1;
  registrationAllowed = false;
  try {
    await Promise.allSettled([...activeRegistrationAttempts]);
    const remoteCleanupSucceeded = await deregisterFcmTokenFromBackend();
    if (!remoteCleanupSucceeded) await disableLocalSellerPushDelivery();
  } finally {
    // A failed remote DELETE must not let the next person on this browser
    // inherit local token pointers or the previous seller's Firebase identity.
    clearStoredFcmTokens();
    writePendingCleanup([]);
    try {
      await signOutSellerFirebaseAuth();
    } catch (error) {
      console.warn("Firebase Auth sign-out during seller logout failed", error);
    }
  }
}

export function getStoredFcmToken(storeId?: string): string | null {
  return localStorage.getItem(storageKey(storeId));
}
